import React, { useState, useEffect } from 'react';
import { 
  Home, 
  Users, 
  MessageSquare, 
  CreditCard, 
  Menu,
  X,
  Settings,
  FileText,
  Bot,
  BarChart3,
  Building2,
  BookOpen
} from 'lucide-react';
import { NavLink } from 'react-router-dom';
import UserProfile from '../auth/UserProfile';
import { useApiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './Sidebar.css';

const Sidebar = ({ isOpen, toggleSidebar, onNavigate, isMobile = false }) => {
  const { user, isAuthenticated } = useAuth();
  const { getUserShowroom } = useApiService();
  const userRole = user?.role;
  const [showroomName, setShowroomName] = useState(null);
  const [isLoadingShowroom, setIsLoadingShowroom] = useState(false);

  // Define menu items based on role
  const getMenuItems = () => {
    const items = [
      { id: 'home', label: 'Home', icon: Home, path: '/' },
    ];

    // superAdmin: Only Users and Showrooms
    if (userRole === 'superAdmin') {
      items.push(
        { id: 'users', label: 'Users', icon: Users, path: '/users' },
        { id: 'showrooms', label: 'Showrooms', icon: Building2, path: '/showrooms' }
      );
    }
    // admin: Users, My Showroom, and all business functionality
    else if (userRole === 'admin') {
      items.push(
        { id: 'users', label: 'Users', icon: Users, path: '/users' },
        { id: 'my-showroom', label: 'My Showroom', icon: Building2, path: '/my-showroom' },
        { id: 'customers', label: 'Customers', icon: Users, path: '/customers' },
        { id: 'cars', label: 'Cars', icon: Users, path: '/cars' },
        { id: 'deals', label: 'Deals', icon: CreditCard, path: '/deals' },
        { id: 'daily-ledger', label: 'Daily Ledger', icon: BookOpen, path: '/daily-ledger' },
        { id: 'reporting', label: 'Reports', icon: BarChart3, path: '/reporting' }
      );
    }
    // user: Only business functionality (no Users, no Showrooms)
    else if (userRole === 'user') {
      items.push(
        { id: 'customers', label: 'Customers', icon: Users, path: '/customers' },
        { id: 'cars', label: 'Cars', icon: Users, path: '/cars' },
        { id: 'deals', label: 'Deals', icon: CreditCard, path: '/deals' },
        { id: 'daily-ledger', label: 'Daily Ledger', icon: BookOpen, path: '/daily-ledger' },
        { id: 'reporting', label: 'Reports', icon: BarChart3, path: '/reporting' }
      );
    }

    return items;
  };

  const menuItems = getMenuItems();

  // Fetch showroom name for non-superAdmin users
  useEffect(() => {
    const fetchShowroomName = async () => {
      if (isAuthenticated && userRole !== 'superAdmin') {
        setIsLoadingShowroom(true);
        try {
          const showroom = await getUserShowroom();
          if (showroom?.showroomName) {
            setShowroomName(showroom.showroomName);
          }
        } catch (error) {
          console.error('Error fetching showroom:', error);
        } finally {
          setIsLoadingShowroom(false);
        }
      }
    };

    fetchShowroomName();
  }, [isAuthenticated, userRole, getUserShowroom]);

  const sidebarClasses = isMobile 
    ? `sidebar sidebar-mobile ${isOpen ? 'sidebar-open' : ''}`
    : 'sidebar';

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && isOpen && (
        <div className="sidebar-overlay" onClick={toggleSidebar}></div>
      )}
      
      {/* Sidebar */}
      <div className={sidebarClasses}>
        {/* Header */}
        <div className="sidebar-header">
          <div className="logo-container">
            {/* <div className="logo-icon">
              <Home size={24} />
            </div> */}
            <h2 className="logo-text">
              {userRole === 'superAdmin' 
                ? 'LEDGER GLIDE' 
                : (showroomName || (isLoadingShowroom ? 'Loading...' : ''))}
            </h2>
            
          </div>
          
          {isMobile && toggleSidebar && (
            <button className="close-btn" onClick={toggleSidebar}>
              <X size={20} />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <ul className="nav-list">
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <li key={item.id} className="nav-item">
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                    end={item.path === '/'}
                    onClick={onNavigate}
                  >
                    <div className="nav-icon">
                      <IconComponent size={20} />
                    </div>
                    <span className="nav-label">{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

      
        {/* User Profile */}
        <UserProfile />
      </div>
    </>
  );
};

export default Sidebar; 